/**
 * Branch-link settings (release 3.3, R3-OI-194): check-in profile, check-in
 * quiet hours and the findings upload limit, for the tenant, a business unit
 * or a device group. Shared by the Device groups panel's policy section and
 * its group detail.
 *
 * Each setting resolves on its own: a group (lowest priority number), the
 * device's business unit, the tenant, the value baked in at install, the
 * agent's default. The controller sends them on the device's next check-in;
 * agents before 3.3 ignore them and the device drawer says so.
 *
 * The editor states the bound an operator relies on: how long a pause, resume
 * or scan-now can take to reach a device under these settings.
 */
import { useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';

import { Button } from '@/components/ui/button';
import type { BranchLinkSettings, CheckinProfile } from '@/lib/api';

import { scanWindowFromInputs } from './EdgePodScanHours';

const INPUT = 'glass-lite rounded px-2 py-1 bg-background/40 border border-border/40 text-xs';

/** The agent's cadences (agent bandwidth.rs, controller network-policy.ts). */
export const LOW_CHATTER_MINUTES = 30;
export const QUIET_KEEPALIVE_MINUTES = 60;
export const CONTROL_TICK_MINUTES = 5;
export const UPLOAD_KB_PER_MINUTE_MAX = 100_000;
/** A lower non-zero limit could keep a first scan from ever finishing (controller network-policy.ts). */
export const UPLOAD_KB_PER_MINUTE_MIN = 16;

/** The longest a pause, resume, scan-now or retirement takes to reach an idle device. */
export function commandReachMinutes(s: Pick<BranchLinkSettings, 'checkinProfile' | 'checkinQuietHours'>): number {
  const base = s.checkinProfile === 'low_chatter' ? LOW_CHATTER_MINUTES : CONTROL_TICK_MINUTES;
  const gap = s.checkinQuietHours ? Math.max(base, QUIET_KEEPALIVE_MINUTES) : base;
  return gap === CONTROL_TICK_MINUTES ? gap : gap + CONTROL_TICK_MINUTES;
}

/** "low chatter, quiet 08:00-15:30, 256 KB/min" or "not set". */
export function branchLinkShort(s: BranchLinkSettings): string {
  const parts: string[] = [];
  if (s.checkinProfile) parts.push(s.checkinProfile === 'low_chatter' ? 'low chatter' : 'standard');
  if (s.checkinQuietHours) parts.push(`quiet ${s.checkinQuietHours}`);
  if (s.uploadKbPerMinute !== null) parts.push(s.uploadKbPerMinute === 0 ? 'no upload limit' : `${s.uploadKbPerMinute} KB/min`);
  return parts.length > 0 ? parts.join(', ') : 'not set';
}

export function hasBranchLink(s: BranchLinkSettings): boolean {
  return s.checkinProfile !== null || s.checkinQuietHours !== null || s.uploadKbPerMinute !== null;
}

type Parsed = { ok: true; value: BranchLinkSettings } | { ok: false; error: string };

/** The form's values as settings: an empty field is "not set at this level". */
export function parseBranchLinkForm(f: { profile: string; quietStart: string; quietEnd: string; upload: string }): Parsed {
  const checkinProfile: CheckinProfile | null =
    f.profile === 'standard' || f.profile === 'low_chatter' ? f.profile : null;
  let checkinQuietHours: string | null = null;
  if (f.quietStart || f.quietEnd) {
    const w = scanWindowFromInputs(f.quietStart, f.quietEnd);
    if ('error' in w) return { ok: false, error: `Quiet hours: ${w.error}` };
    checkinQuietHours = w.window;
  }
  let uploadKbPerMinute: number | null = null;
  if (f.upload.trim() !== '') {
    const n = Number(f.upload.trim());
    if (!Number.isInteger(n) || n < 0 || n > UPLOAD_KB_PER_MINUTE_MAX || (n > 0 && n < UPLOAD_KB_PER_MINUTE_MIN)) {
      return {
        ok: false,
        error: `Upload limit: 0 (no limit) or a whole number of KB a minute from ${UPLOAD_KB_PER_MINUTE_MIN} to ${UPLOAD_KB_PER_MINUTE_MAX}.`,
      };
    }
    uploadKbPerMinute = n;
  }
  return { ok: true, value: { checkinProfile, checkinQuietHours, uploadKbPerMinute } };
}

function split(w: string | null): [string, string] {
  if (!w) return ['', ''];
  const [a, b] = w.split('-');
  return [a ?? '', b ?? ''];
}

/**
 * The three inputs, pre-filled with what the level sets now. `onSave` gets the
 * whole set (each key a value or null); `onClear` clears all three.
 */
export function BranchLinkEditor(props: {
  label: string;
  current: BranchLinkSettings | null;
  disabled?: boolean;
  /** The caller's reason field; the editor refuses to save without one. */
  reason: string;
  onSave: (value: BranchLinkSettings) => Promise<void>;
  testId?: string;
}): JSX.Element {
  const cur = props.current;
  const [profile, setProfile] = useState(cur?.checkinProfile ?? '');
  const [quietStart, setQuietStart] = useState(split(cur?.checkinQuietHours ?? null)[0]);
  const [quietEnd, setQuietEnd] = useState(split(cur?.checkinQuietHours ?? null)[1]);
  const [upload, setUpload] = useState(cur?.uploadKbPerMinute == null ? '' : String(cur.uploadKbPerMinute));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // A different level picked: show what it sets.
  useEffect(() => {
    setProfile(cur?.checkinProfile ?? '');
    const [a, b] = split(cur?.checkinQuietHours ?? null);
    setQuietStart(a);
    setQuietEnd(b);
    setUpload(cur?.uploadKbPerMinute == null ? '' : String(cur.uploadKbPerMinute));
    setMsg(null);
  }, [cur?.checkinProfile, cur?.checkinQuietHours, cur?.uploadKbPerMinute]);

  const parsed = parseBranchLinkForm({ profile, quietStart, quietEnd, upload });
  const reach = parsed.ok ? commandReachMinutes(parsed.value) : null;

  async function run(value: BranchLinkSettings, clearing: boolean): Promise<void> {
    if (props.reason.trim().length < 3) return setMsg({ ok: false, text: 'Give a reason (it is kept in the audit log).' });
    if (clearing && !window.confirm(`Clear the branch-link settings of ${props.label}? Its devices fall back to the next level.`)) return;
    setBusy(true);
    setMsg(null);
    try {
      await props.onSave(value);
      setMsg({ ok: true, text: 'Saved. Devices take it on their next check-in.' });
    } catch (e) {
      setMsg({ ok: false, text: e instanceof Error ? e.message : 'could not save the settings' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-1.5 text-xs" data-testid={props.testId ?? 'branch-link-editor'}>
      <div className="flex flex-wrap items-center gap-2">
        <label className="inline-flex items-center gap-1">
          <span className="text-muted-foreground">Check-in</span>
          <select aria-label={`${props.label} check-in profile`} value={profile} onChange={(e) => setProfile(e.target.value)} className={INPUT} disabled={props.disabled}>
            <option value="">Not set here</option>
            <option value="standard">Standard (every {CONTROL_TICK_MINUTES} min)</option>
            <option value="low_chatter">Low chatter (every {LOW_CHATTER_MINUTES} min)</option>
          </select>
        </label>
        <span className="inline-flex items-center gap-1">
          <span className="text-muted-foreground">Quiet hours</span>
          <input type="time" aria-label={`${props.label} quiet hours from`} value={quietStart} onChange={(e) => setQuietStart(e.target.value)} className={INPUT} disabled={props.disabled} />
          <span className="text-muted-foreground">to</span>
          <input type="time" aria-label={`${props.label} quiet hours until`} value={quietEnd} onChange={(e) => setQuietEnd(e.target.value)} className={INPUT} disabled={props.disabled} />
        </span>
        <label className="inline-flex items-center gap-1">
          <span className="text-muted-foreground">Upload limit</span>
          <input
            inputMode="numeric"
            aria-label={`${props.label} upload limit, KB a minute`}
            placeholder="KB/min, 0 = none"
            value={upload}
            onChange={(e) => setUpload(e.target.value)}
            className={`${INPUT} w-28`}
            disabled={props.disabled}
          />
        </label>
        <Button size="sm" disabled={props.disabled || busy || !parsed.ok} onClick={() => parsed.ok && void run(parsed.value, false)}>
          {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Save className="mr-1 h-3 w-3" />}
          Save settings
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={props.disabled || busy || !cur || !hasBranchLink(cur)}
          onClick={() => void run({ checkinProfile: null, checkinQuietHours: null, uploadKbPerMinute: null }, true)}
        >
          Clear settings
        </Button>
      </div>
      <p className="text-muted-foreground" data-testid="branch-link-reach">
        {parsed.ok
          ? `With these settings a pause, resume or scan-now reaches an idle device within ${reach} minutes${
              parsed.value.checkinQuietHours
                ? ` inside the quiet hours (${commandReachMinutes({ ...parsed.value, checkinQuietHours: null })} minutes outside them)`
                : ''
            } with the default 5-minute control task. A device about to scan always checks in first; a scan already running stops at its next upload, because the console refuses a paused device's uploads at once.`
          : parsed.error}
      </p>
      {msg ? (
        <p role={msg.ok ? 'status' : 'alert'} className={msg.ok ? 'text-success' : 'text-destructive'}>
          {msg.text}
        </p>
      ) : null}
    </div>
  );
}
